import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import userApi from "api/user/user.api";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setUser } from "../../redux/authSlice";

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    const emailFromURL = searchParams.get("email");
    if (emailFromURL) {
      setEmail(emailFromURL);
    }
  }, [searchParams]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpValues];
    newOtp[index] = value.slice(-1);
    setOtpValues(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim();
    if (!/^\d+$/.test(pasteData)) return;
    const pasteArray = pasteData.split("").slice(0, 6);
    const newOtp = [...otpValues];
    pasteArray.forEach((char, index) => {
      newOtp[index] = char;
    });
    setOtpValues(newOtp);
    const nextIndex = pasteArray.length >= 6 ? 5 : pasteArray.length;
    if (inputsRef.current[nextIndex]) {
      inputsRef.current[nextIndex].focus();
    }
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otp = otpValues.join("");

    try {
      const res = await userApi.verifyEmail(otp, email);

      console.log({ res });
      setMessage(res.message || "Email verified successfully!");
      toast.success(res.message);
      // Delay navigation so toast can be seen

      dispatch(setUser(res.user)); // Make sure your verifyEmail API returns user data

      setTimeout(() => {
        navigate("/");
      }, 2000); // 2-second delay
      setError(false);
    } catch (error) {
      setMessage(error.message || "Verification failed. Try again.");
      setError(true);
    }
  };

  const handleTryAgain = () => {
    setOtpValues(["", "", "", "", "", ""]);
    setMessage("");
    setError(false);
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      const res = await userApi.resendOtp(email);
      setMessage(res.message || "New OTP sent. Please check your email.");
      setOtpValues(["", "", "", "", "", ""]);
      setError(false);
      if (inputsRef.current[0]) {
        inputsRef.current[0].focus();
      }
    } catch (error) {
      setMessage(error.message || "Failed to resend OTP.");
      setError(true);
    }
    setResendLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dry p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-drylight p-6 rounded-lg shadow-md w-full max-w-md text-center"
      >
        <img
          src="/image/logo_dark.png" // update logo path if needed
          alt="Logo"
          className="w-60 mx-auto mb-4"
        />
        <h2 className="text-2xl font-semibold mb-2">Enter OTP</h2>
        <p className="text-sm text-textGray mb-6">
          Enter the verification code sent to your email.
        </p>

        <div className="flex justify-center space-x-2 mb-6">
          {otpValues.map((value, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              maxLength="1"
              value={value}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              disabled={message && !error} // Disable after verified
              className="w-12 h-12 border rounded text-center text-xl focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          ))}
        </div>

        {!message || error ? (
          <button
            type="submit"
            className="w-full bg-red-500 text-white p-3 rounded hover:bg-red-600 transition "
            style={{ background: "linear-gradient(-45deg, #f87171, #991b1b)" }}
          >
            Verify OTP
          </button>
        ) : null}

        {message && (
          <div className="mt-4 text-center">
            <p
              className={`text-sm ${error ? "text-red-500" : "text-green-600"}`}
            >
              {message}
            </p>

            {error && (
              <>
                <button
                  type="button"
                  onClick={handleTryAgain}
                  className="mt-4 inline-block bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-6 rounded transition"
                >
                  Try Again
                </button>

                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendLoading}
                  className="mt-4 block text-blue-600 underline text-sm hover:text-blue-800"
                >
                  {resendLoading ? "Sending..." : "Didn't receive OTP? Resend"}
                </button>
              </>
            )}
          </div>
        )}

        <p className="mt-6 text-sm text-gray-500">
          Already have an account?{" "}
          <a href="/login" className="text-blue-600 hover:underline">
            Login
          </a>
        </p>
      </form>
    </div>
  );
};

export default VerifyEmailPage;
